import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';

import { useFeatureGate } from '../hooks/useFeatureGates';

/** Which side of the content the assistant panel renders on at `xl+`. */
export type AssistantPlacement = 'left' | 'right';

/**
 * The viewport breakpoint at which `EntityPaneBody` switches from one to two
 * columns. Shifts to `2xl` while the assistant panel occupies horizontal
 * space so the narrowed content track does not try to host two columns until
 * there is genuinely room.
 */
export type ContentColumnsBreakpoint = 'xl' | '2xl';

interface IAssistantSidebarContextValue {
  /** Whether `feat-assistant` passes for the viewer. */
  isEnabled: boolean;
  /** Whether the panel is currently expanded. Open by default when enabled. */
  isOpen: boolean;
  /** `right` only when enabled AND `exp-assistant-right-placement` passes. */
  placement: AssistantPlacement;
  /** `2xl` while the panel consumes space (enabled + open), else `xl`. */
  columnsBreakpoint: ContentColumnsBreakpoint;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

/**
 * Default reflects "no assistant" — gate off, today's column behavior. Lets
 * `EntityPaneBody` (and its pane Storybook stories) consume
 * {@link useContentColumnsBreakpoint} without being wrapped in the provider.
 */
const DEFAULT_VALUE: IAssistantSidebarContextValue = {
  isEnabled: false,
  isOpen: false,
  placement: 'left',
  columnsBreakpoint: 'xl',
  toggle: () => {},
  open: () => {},
  close: () => {},
};

const AssistantSidebarContext = React.createContext<IAssistantSidebarContextValue>(DEFAULT_VALUE);

/** `right` only when the assistant is enabled AND the right-placement gate passes. */
export const deriveAssistantPlacement = (isEnabled: boolean, isRightPlacement: boolean): AssistantPlacement =>
  isEnabled && isRightPlacement ? 'right' : 'left';

/** `2xl` while the panel consumes content width (enabled + open), else `xl`. */
export const deriveContentColumnsBreakpoint = (isEnabled: boolean, isOpen: boolean): ContentColumnsBreakpoint =>
  isEnabled && isOpen ? '2xl' : 'xl';

interface IAssistantSidebarProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the cross-cutting state for the assistant side panel: whether it is
 * available (`feat-assistant`), which placement it uses
 * (`exp-assistant-right-placement` → right module, else left sidebar), and
 * whether it is currently open. `isOpen` is in-memory and starts `true` so the
 * panel is open by default for gated viewers; it resets on reload (the app has
 * no persisted-UI-state pattern).
 *
 * Consumed by `HomeScene` (renders the panel + reflows content) and
 * `MantleAssistantNavButton` (toggles it) via {@link useAssistantSidebar}, and
 * by `EntityPaneBody` via the narrower {@link useContentColumnsBreakpoint} so a
 * generic layout primitive depends only on "which breakpoint do my columns
 * use," not on the assistant itself.
 */
const AssistantSidebarProvider = ({ children }: IAssistantSidebarProviderProps) => {
  const isEnabled = useFeatureGate(FeatureGate.Assistant);
  const isRightPlacement = useFeatureGate(FeatureGate.AssistantRightPlacement);
  const [isOpen, setIsOpen] = React.useState(true);

  const toggle = React.useCallback(() => setIsOpen((prev) => !prev), []);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  const value = React.useMemo<IAssistantSidebarContextValue>(
    () => ({
      isEnabled,
      isOpen,
      placement: deriveAssistantPlacement(isEnabled, isRightPlacement),
      columnsBreakpoint: deriveContentColumnsBreakpoint(isEnabled, isOpen),
      toggle,
      open,
      close,
    }),
    [isEnabled, isRightPlacement, isOpen, toggle, open, close]
  );

  return <AssistantSidebarContext.Provider value={value}>{children}</AssistantSidebarContext.Provider>;
};

export default AssistantSidebarProvider;

/**
 * Reads the assistant side-panel state. Used by the shell (`HomeScene`, to
 * render/place the panel) and the nav toggle button. Outside the provider it
 * returns {@link DEFAULT_VALUE} (gate off, panel closed) so the no-op default
 * is safe in Storybook.
 *
 * @returns The full context value — `isEnabled`, `isOpen`, `placement`,
 *   `columnsBreakpoint`, and the `toggle` / `open` / `close` setters.
 */
export const useAssistantSidebar = (): IAssistantSidebarContextValue => React.useContext(AssistantSidebarContext);

/**
 * Narrow read of just the column breakpoint, for `EntityPaneBody`. Layout-
 * neutral by name so a generic layout primitive depends on "which breakpoint
 * do my columns use," not on the assistant.
 *
 * @returns `'2xl'` while the panel occupies content width (enabled + open),
 *   otherwise `'xl'`.
 */
export const useContentColumnsBreakpoint = (): ContentColumnsBreakpoint =>
  React.useContext(AssistantSidebarContext).columnsBreakpoint;
